import { useCallback, useEffect } from 'react';

// Configuration
const UNTHREAD_BASE_URL = 'https://spotify.unthread.io';
const UNTHREAD_WIDGET_ID = '71d1916f-d674-414e-b3f3-283d13de12f3';

interface UnthreadInAppChatSettings {
  baseUrl: string;
  widgetId: string;
}

interface UnthreadQueue {
  (...args: any[]): void;
  q: any[][];
  c: (args: any[]) => void;
}

declare global {
  interface Window {
    $unthreadInAppChatSettings?: UnthreadInAppChatSettings;
    $unthread?: {
      inAppChat?: UnthreadQueue;
    };
  }
}

export const useUnthread = () => {
  useEffect(() => {
    // Only load Unthread if credentials are configured
    if (!UNTHREAD_BASE_URL || !UNTHREAD_WIDGET_ID) {
      // eslint-disable-next-line no-console
      console.warn('Unthread credentials not configured');
      return undefined;
    }

    // Check if already initialized
    if (window.$unthread?.inAppChat) {
      return undefined;
    }

    // Initialize Unthread settings
    window.$unthreadInAppChatSettings = {
      baseUrl: UNTHREAD_BASE_URL,
      widgetId: UNTHREAD_WIDGET_ID,
    };

    // Create queue function
    const u = function queueFunction(this: UnthreadQueue, ...args: any[]) {
      this.c(args);
    } as UnthreadQueue;
    u.q = [];
    u.c = (args: any[]) => {
      u.q.push(args);
    };

    // Initialize $unthread namespace
    if (!window.$unthread) {
      window.$unthread = {};
    }
    window.$unthread.inAppChat = u;

    // Load Unthread script
    const script = document.createElement('script');
    script.src = `${UNTHREAD_BASE_URL}/widget/js/wrapper.js`;
    script.defer = true;
    script.async = true;

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(script, firstScript);

    return () => {
      // Cleanup script on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const openChat = useCallback((user?: { name: string; email: string }) => {
    if (!UNTHREAD_BASE_URL || !UNTHREAD_WIDGET_ID) {
      // eslint-disable-next-line no-console
      console.warn(
        'Unthread credentials not configured. Please add your base URL and widget ID.',
      );
      return;
    }

    if (window.$unthread?.inAppChat) {
      if (user) {
        window.$unthread.inAppChat('start', { user });
      } else {
        window.$unthread.inAppChat('start');
      }

      window.$unthread.inAppChat('toggle');
    } else {
      // eslint-disable-next-line no-console
      console.warn('Unthread not loaded yet');
    }
  }, []);

  const closeChat = useCallback(() => {
    if (window.$unthread?.inAppChat) {
      window.$unthread.inAppChat('close');
    }
  }, []);

  const toggleChat = useCallback(() => {
    if (window.$unthread?.inAppChat) {
      window.$unthread.inAppChat('toggle');
    }
  }, []);

  return {
    openChat,
    closeChat,
    toggleChat,
    isConfigured: !!(UNTHREAD_BASE_URL && UNTHREAD_WIDGET_ID),
  };
};
